/**
 * useLayoutMode - Adaptive layout based on device + user role
 *
 * Three layout modes:
 * - 'desktop'     : Full interface, Windows-style taskbar (default)
 * - 'tablet-full' : Full interface, touch-optimized (manager/admin on tablet)
 * - 'field'       : Streamlined interface for field technicians (technician on tablet/mobile)
 *
 * Logic:
 * 1. Manual override from localStorage takes priority
 * 2. technician + (tablet || mobile) + touch → 'field'
 * 3. any full-access role + tablet + touch → 'tablet-full'
 * 4. everything else → 'desktop'
 */

import { computed, ref } from 'vue'
import { useDevice } from './useDevice'
import { useAuthStore } from '@/core/stores/auth'

export type LayoutMode = 'desktop' | 'tablet-full' | 'field'

const STORAGE_KEY = 'gleem-layout-mode'

// Manual override — shared across all instances
const manualOverride = ref<LayoutMode | null>(
  (typeof localStorage !== 'undefined'
    ? localStorage.getItem(STORAGE_KEY) as LayoutMode | null
    : null)
)

export function useLayoutMode() {
  const { isMobile, isTabletDevice, isTouchDevice } = useDevice()
  const authStore = useAuthStore()

  const autoMode = computed<LayoutMode>(() => {
    const role = authStore.user?.role

    // Technician on touch tablet/mobile → field mode
    if (role === 'technician' && isTouchDevice.value && (isMobile.value || isTabletDevice.value)) {
      return 'field'
    }

    // Full-access role on physical tablet (touch + 768–1400px) → tablet-full
    if (isTabletDevice.value && role && role !== 'technician') {
      return 'tablet-full'
    }

    // Mobile non-technician → also tablet-full (touch-optimized)
    if (isMobile.value && isTouchDevice.value && role && role !== 'technician') {
      return 'tablet-full'
    }

    return 'desktop'
  })

  const layoutMode = computed<LayoutMode>(() => {
    return manualOverride.value ?? autoMode.value
  })

  // Convenience booleans
  const isFieldMode = computed(() => layoutMode.value === 'field')
  const isTabletFull = computed(() => layoutMode.value === 'tablet-full')
  const isDesktopMode = computed(() => layoutMode.value === 'desktop')
  const isTouchMode = computed(() => isFieldMode.value || isTabletFull.value)

  function setLayoutMode(mode: LayoutMode | null) {
    manualOverride.value = mode
    if (mode) {
      localStorage.setItem(STORAGE_KEY, mode)
    } else {
      localStorage.removeItem(STORAGE_KEY)
    }
  }

  function resetToAuto() {
    setLayoutMode(null)
  }

  return {
    layoutMode,
    autoMode,
    isFieldMode,
    isTabletFull,
    isDesktopMode,
    isTouchMode,
    manualOverride,
    setLayoutMode,
    resetToAuto,
  }
}

export default useLayoutMode
